import {AfterViewInit, Directive, inject, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DxDataGridComponent} from "devextreme-angular";
import {DataSources, ScreenService} from "@services";
import {Workbook} from "exceljs";
import {exportDataGrid as exportDataGridToXLSX} from "devextreme/common/export/excel";
import {DxDataGridTypes} from "devextreme-angular/ui/data-grid";
import { saveAs } from 'file-saver-es';

export interface FilterConfig {
    field: string;
    op?: string;
    gridField?: string;
    customFilter?: (value: any, op?: string) => any;
}

@Directive()
export abstract class GridPageComponent<T> implements AfterViewInit {
    protected dataSources = inject(DataSources);
    protected screen = inject(ScreenService);
    private deepLinkRoute = inject(ActivatedRoute);
    protected filterConfig: FilterConfig[] = [];
    protected type!: { new(): T };
    protected exportName: string = "Report";

    protected enableDeepLinkDateFilter = false;
    protected dateRangeFromField = 'lastSeenInUpload';
    protected dateRangeToField = 'created';

    nameOrEmailFilter = this.userNameOrEmailFilter('employee');

    protected init(type: { new(): T }) {
        this.type = type;
        this.filterModel = new this.type();
    }

    ngAfterViewInit(): void {
        if (!this.enableDeepLinkDateFilter) return;

        const queryParams = this.deepLinkRoute.snapshot.queryParamMap;
        const dateStr = queryParams.get('date');
        const fromStr = queryParams.get('from');
        const toStr = queryParams.get('to');
        const graceInterval = 60_000;

        let from: Date | null = null;
        let to: Date | null = null;

        if (fromStr && toStr) {
            const fromDate = new Date(fromStr);
            const toDate = new Date(toStr);

            if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
                from = new Date(fromDate.getTime() - graceInterval);
                to = new Date(toDate.getTime() + graceInterval);
            }
        } else if (dateStr) {
            const date = new Date(dateStr);

            if (!isNaN(date.getTime())) {
                from = new Date(date.getTime() - graceInterval);
                to = new Date(date.getTime() + graceInterval);
            }
        }

        if (!from || !to) return;

        setTimeout(() => {
            const grid = this.dataGrid?.instance;
            if (!grid) return;

            grid.clearFilter();
            grid.filter([
                [this.dateRangeToField, '<=', to.toISOString()],
                'and',
                [this.dateRangeFromField, '>=', from.toISOString()]
            ]);
        });
    }

    @ViewChild(DxDataGridComponent, {static: false}) dataGrid: DxDataGridComponent;

    isFilterPanelOpened: boolean = false;
    isPopupOpened: boolean;
    selectedRow: T | null = null;
    filterModel!: T;

    gridFieldName = (name: keyof T): string => name as string;
    filterModelName = (name: keyof T): string => name as string;

    getCellData(cellInfo: { data: T }) {
        return cellInfo.data;
    }

    protected userNameOrEmailFilter(field: string) {
        return (filterValue: any, selectedFilterOperation: string) => {
            if (!filterValue) {
                return null;
            }

            return [
                [`${field}.name`, selectedFilterOperation || 'contains', filterValue],
                'or',
                [`${field}.email`, selectedFilterOperation || 'contains', filterValue]
            ];
        };
    }

    resetGrid = async () => {
        this.dataGrid.instance.clearFilter();
        this.dataGrid.instance.clearSorting();
        this.dataGrid.instance.state(null)
        this.clearFilters();
    };

    toggleFilters = () => {
        this.isFilterPanelOpened = true;
    };

    applyFilters = () => {
        const grid = this.dataGrid.instance;
        const filters: any[] = [];

        this.filterConfig.forEach(config => {
            const value = this.getNestedValue(this.filterModel, config.field);

            if (value !== null && value !== undefined && value !== '') {
                if (typeof value === 'string' && value.trim() === '') {
                    return;
                }

                if (typeof value === 'boolean' && value === false) {
                    return;
                }

                if (config.customFilter) {
                    filters.push(config.customFilter(value, config.op));
                    return;
                }

                const op = config.op || '=';
                const gridField = config.gridField || config.field;
                const filterValue = typeof value === 'string' ? value.trim() : value;

                filters.push([gridField, op, filterValue]);
            }
        });

        let finalFilter: any = null;
        if (filters.length === 1) {
            finalFilter = filters[0];
        } else if (filters.length > 1) {
            finalFilter = filters.reduce((acc, f) => acc ? [acc, 'and', f] : f, null);
        }

        if (!finalFilter) {
            grid.clearFilter();
        } else {
            grid.filter(finalFilter);
        }
    };

    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((o, p) => (o ? o[p] : null), obj);
    }

    clearFilters = () => {
        this.filterModel = new this.type();
        this.dataGrid.instance.clearFilter();
    }

    onPinnedChange = () => {
        this.dataGrid.instance.updateDimensions();
    };

    onExporting(e) {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(this.exportName);

        exportDataGridToXLSX({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${this.exportName}.xlsx`);
            });
        });
        e.cancel = true;
    }

    customizeColumns = (columns: any[]): void => {
        const isLarge = this.screen.sizes['screen-large'];
        if (isLarge) {
            columns.forEach(column => {
                column.hidingPriority = undefined;
            });
        } else {
            columns.forEach(column => {
                column.hidingPriority = columns.length - 1 - column.index;
            });
        }
    }

    onEdit = (e: any) => {
        const event = e as { row: { data: T } };
        this.selectedRow = event.row.data;
        this.isPopupOpened = true;
    }

    onView = (e: any) => {
        const event = e as { row: { data: T } };
        this.selectedRow = event.row.data;
        this.isPopupOpened = true;
    }

    addRow() {
        this.selectedRow = null;
        this.isPopupOpened = true;
    }

    async onAdd() {
        await this.dataGrid.instance.refresh();
    }

    onRowDblClick($event: DxDataGridTypes.RowDblClickEvent) {
        this.selectedRow = $event.data;
        this.isPopupOpened = true;
    }
}
